import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, ChevronRight, IdCard, QrCode, Search, UserPlus, Users } from 'lucide-react';
import { Drawer, DrawerContent } from '../ui/drawer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScreenshotIntakeButton } from '../IntakeHelpers';
import MakeIntrosButton from '../home/MakeIntrosButton';
import { SettingsPhoneWaitlists } from '../auth/PhoneAppOffer';
import { useAuth } from '../../lib/auth';
import useStartGroupShare from '../../hooks/useStartGroupShare';

const GROUP_INTROS_SUB = 'One QR code lets multiple people share and receive each others\u2019 info.';
const PHOTO_SUB = 'Photo of business card or screenshot of new contact';
const SCAN_USER_COPY = 'The live camera scan is in the PeopleiKnow iOS app.';

function MenuRow({ icon: Icon, label, sub }) {
  return (
    <span className="flex w-full items-center gap-3 text-left">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-nav-blue/10 text-nav-blue">
        <Icon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">{label}</span>
        {sub ? <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{sub}</span> : null}
      </span>
      <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
    </span>
  );
}

function RowButton({ testId, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full rounded-xl px-2 py-2 text-left hover:bg-secondary"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function AddMenuSheet({ open, onOpenChange, onMyCard }) {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { start, busy } = useStartGroupShare();
  const [scanOpen, setScanOpen] = useState(false);
  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
        <DrawerContent className="max-h-[92dvh] overflow-y-auto px-3 pb-4" data-testid="add-menu-sheet">
          <button type="button" className="sr-only" data-testid="add-menu-close" onClick={() => onOpenChange(false)}>
            Close
          </button>
          <h2 className="px-2 pb-1 pt-2 font-heading text-lg font-extrabold">Add new contact</h2>
          <RowButton testId="add-menu-manual" onClick={() => go('/contacts/new')}>
            <MenuRow icon={UserPlus} label="Add manually" sub="Type in a new contact" />
          </RowButton>
          <ScreenshotIntakeButton
            testId="add-menu-scan"
            label="Photo or screenshot"
            className="rounded-xl px-2 py-2 hover:bg-secondary"
            onCreated={() => onOpenChange(false)}
          >
            {({ pending }) => (
              <MenuRow icon={Camera} label="Photo or screenshot" sub={pending ? 'Reading…' : PHOTO_SUB} />
            )}
          </ScreenshotIntakeButton>
          <RowButton testId="add-menu-search-pik" onClick={() => go('/search-users')}>
            <MenuRow icon={Search} label="Search PeopleiKnow user" sub="Find someone already on PeopleiKnow" />
          </RowButton>
          <RowButton testId="add-menu-scan-user" onClick={() => setScanOpen(true)}>
            <MenuRow icon={QrCode} label="Scan a PeopleiKnow user" sub="Scan their card QR code to connect instantly" />
          </RowButton>
          <RowButton testId="add-menu-introductions" onClick={() => go('/intros')}>
            <span className="flex w-full items-center justify-between py-1">
              <span className="font-heading text-lg font-extrabold text-foreground">Introductions</span>
              <ChevronRight size={18} className="text-muted-foreground" />
            </span>
          </RowButton>
          <MakeIntrosButton
            testId="add-menu-introduce"
            ariaLabel="Introduce two people I know"
            className="flex w-full rounded-xl px-2 py-2 text-left hover:bg-secondary"
          >
            <MenuRow icon={Users} label="Introduce two people I know" sub="Send two contacts each other's card" />
          </MakeIntrosButton>
          <RowButton testId="add-menu-group" onClick={() => { onOpenChange(false); start(); }}>
            <MenuRow
              icon={QrCode}
              label="Group Intros session"
              sub={busy ? 'Starting...' : GROUP_INTROS_SUB}
            />
          </RowButton>
          <RowButton testId="add-menu-my-card" onClick={() => { onOpenChange(false); onMyCard?.(); }}>
            <MenuRow icon={IdCard} label="Open my card (QR code)" sub="They can quickly add you and send you their info." />
          </RowButton>
        </DrawerContent>
      </Drawer>
      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent data-testid="add-menu-scan-user-dialog">
          <DialogHeader>
            <DialogTitle>Scan a PeopleiKnow user</DialogTitle>
          </DialogHeader>
          <p className="text-sm font-medium leading-relaxed" data-testid="add-menu-scan-user-copy">
            {SCAN_USER_COPY}
          </p>
          <SettingsPhoneWaitlists user={user} refreshUser={refreshUser} variant="import" />
        </DialogContent>
      </Dialog>
    </>
  );
}
